import {
  AfterViewInit,
  Component,
  OnInit, OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReplaySubject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { RoomsService } from 'src/app/services/rooms/rooms.service';
import { RoomsCacheService } from 'src/app/services/rooms-cache/rooms-cache.service';
import { CreationalPagesLayoutService } from 'src/app/services/ui/creational-pages-layout.service';
import { ToastService } from 'src/app/utils/toast.service';

@Component({
  templateUrl: './new-class-page.component.html',
  styleUrls: ['./new-class-page.component.scss'],
})
export class NewClassPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private classId!: string | null;
  three: any = [];
  errors: { title: string }[] = [];
  inProcess = false;
  public organizations: any[] = [];
  public selectedOrgs: any = [];
  public programs: { label: string; value: string | number }[] = [];
  public reports: { label: string; value: string | number }[] = [];
  public teachers: { label: string; value: string | number }[] = [];
  public levels: { label: string; value: number }[] = [];
  public levelsDisabledReason = '';
  room: any = {
    name: '',
    internal_name: '',
    description: '',
    questionary: '',
    teacher: '',
    report: '',
    level: null,
  };
  institutions: any = null;
  report_types: any[] = [];
  private destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    public router: Router,
    public roomsService: RoomsService,
    public creationalPagesLayoutService: CreationalPagesLayoutService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private toastService: ToastService,
    private roomsCache: RoomsCacheService
  ) {
    this.creationalPagesLayoutService.layoutConfig.next({
      headerTitle: this.translate.instant('shared-components.new-class.header-title'),
      headerTitleIcon: 'icon_create',
      footerConfig: {
        submitButtonText: this.translate.instant('shared-components.new-class.save-button'),
        dismissButtonText: this.translate.instant('shared-components.new-class.cancel-button'),
      },
    });
  }

  ngOnInit(): void {
    this.classId = this.activatedRoute.snapshot.paramMap.get('classId') || '';

    if (this.creationalPagesLayoutService.institutionId.getValue()) {
      this.getOrganization(
        this.creationalPagesLayoutService.institutionId.getValue() ?? ''
      );
    }

    if (this.classId) {
      this.roomsService.getRoom(this.classId).subscribe((data) => {
        this.room = {
          id: data.id,
          description: data.description,
          name: data.roomName,
          questionary: data.questionaries[0].id,
          report: data.reports[0].id,
          teacher: data.teacher.id,
          organization: data.organization,
          level: data.level?.id ?? data.level ?? null,
        };
      });
    }
  }

  ngAfterViewInit() {
    this.creationalPagesLayoutService.onSubmit
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value) {
          this.handleSubmit();
        }
      });
    this.creationalPagesLayoutService.onDismiss
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value) {
          this.router.navigate(['dashboard/institution/classes/list']);
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getOrganization(institutionId: string) {
    this.roomsService.getInstitution(institutionId).subscribe({
      next: (data) => {
        this.programs = data.questionaries.map((questionary: any) => ({
          label: questionary.name,
          value: questionary.id,
        }));

        this.teachers = data.users
          .filter(
            (user: any) =>
              user.user_type === 'Teacher' || user.user_type === 'School'
          )
          .map((user: any) => ({
            label: user.first_name + user.last_name,
            value: user.id,
          }));

        this.reports = data.reports.map((questionary: any) => ({
          label: questionary.name,
          value: questionary.id,
        }));

        const countryId = data.country ?? null;
        if (countryId) {
          this.getRoomLevels(countryId);
        } else {
          this.levelsDisabledReason = this.translate.instant(
            'shared-components.new-class.level-no-country'
          );
        }
      },
      error: () => {
        // this.errorsService.handleErrors(e);
      },
    });
  }

  getRoomLevels(countryId: number) {
    this.roomsService.getRoomLevels(countryId).subscribe({
      next: (data) => {
        const results = data.results ?? data;
        this.levels = results.map((level: any) => ({
          label: level.name,
          value: level.id,
        }));
        if (this.levels.length === 0) {
          this.levelsDisabledReason = this.translate.instant(
            'shared-components.new-class.level-no-options'
          );
        }
      },
      error: () => {
        this.levelsDisabledReason = this.translate.instant(
          'shared-components.new-class.level-no-options'
        );
      },
    });
  }

  public handleSubmit() {
    if (!this.isValidForm(this.room)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (this.classId) {
      this.roomsService
        .updateRoom({
          id: this.room.id,
          name: this.room.name,
          description: this.room.description,
          questionary_ids: [this.room.questionary],
          teacher_id: this.room.teacher,
          report_ids: [this.room.report],
          organization: this.room.organization,
          level: this.room.level || null,
        })
        .subscribe((response) => {
          console.log('RESPONSE ', response);

          if (response.warnings && response.warnings.length > 0) {
            if (response.warnings[0] === 'program_cannot_be_changed') {
              this.toastService.showNotificationModal(
                this.translate.instant('shared-components.new-class.cant-update-program-modal-label'),
                '',
                'warning',
                this.translate.instant('shared-components.new-class.cant-update-program-modal-button')
              )
            }
          }

          this.roomsCache.invalidate(this.room.organization);
          this.roomsCache.invalidateRoom(this.room.id);
          this.router.navigate(['dashboard/institution/classes/list']);
        });
    } else {
      this.roomsService
        .createRoom({
          name: this.room.name,
          description: this.room.description,
          questionary_ids: [this.room.questionary],
          participant_ids: [this.room.teacher],
          report_ids: [this.room.report],
          organization:
            this.creationalPagesLayoutService.institutionId.getValue(),
          level: this.room.level || null,
        })
        .subscribe((response) => {
          console.log('RESPONSE ', response);
          const orgId = this.creationalPagesLayoutService.institutionId.getValue();
          if (orgId) this.roomsCache.invalidate(orgId);
          this.router.navigate(['dashboard/institution/classes/list']);
        });
    }
  }

  isValidForm(room: any) {
    this.errors = [];
    let isValidForm = true;

    //?? Check mandatory fields
    if (!room.name || room.name.trim() === '') {
      this.errors.push({
        title: this.translate.instant('shared-components.new-class.name-required')
      });
      isValidForm = false;
    }

    if (!room.questionary || room.questionary === '') {
      this.errors.push({
        title: this.translate.instant('shared-components.new-class.program-required')
      });
      isValidForm = false;
    }

    if (!room.teacher || room.teacher === '') {
      this.errors.push({
        title: this.translate.instant('shared-components.new-class.teacher-required')
      });
      isValidForm = false;
    }

    if (!room.report || room.report === '') {
      this.errors.push({
        title: this.translate.instant('shared-components.new-class.report-required')
      });
      isValidForm = false;
    }

    if (!this.classId && (room.level === null || room.level === undefined || room.level === '')) {
      this.errors.push({
        title: this.translate.instant('shared-components.new-class.level-required')
      });
      isValidForm = false;
    }

    return isValidForm;
  }

  resetForm() {
    this.room = {
      name: '',
      description: '',
      program: '',
      teacher: '',
      report: '',
    };
  }

  public handleSelectionChange(selectedOptions: Record<string, string>[]) {
    console.debug('[selectedOptions]', selectedOptions);
    this.selectedOrgs = selectedOptions.map((option: any) => option.value);
  }
}
